import { formatDistance, getDogAgeDisplayVerbose } from "@wags/catalog/pure.ts";
import type { ProviderData } from "@/features/providers";
import { usePrefetchListings } from "@/features/listings";
import { useWagsListingsLiveQuery } from "@/features/listings";
import { calculateDistance, useUserLocation } from "@/features/viewer-location";
import {
    useProviders,
    useProviderListingReferencesInitialPage,
    useWagsOrganizationsReviewsLiveQuery,
} from "@/features/providers";

const RELATIVE_TIME = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
type ProviderOrganization = NonNullable<
    ReturnType<typeof useProviders>["data"]
>[number];

function monthsAgo(date: string | null | undefined): string | undefined {
    if (!date) return undefined;
    const months = Math.round(
        (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24 * 30),
    );
    return RELATIVE_TIME.format(-Math.max(months, 0), "month");
}

function providerIdentityData(
    org: ProviderOrganization,
    city: string | undefined,
    distanceMiles: number | null,
    reviewCount: number | undefined,
    listingCount: number | undefined,
) {
    return {
        providerName: org.kennel_name || org.name || undefined,
        isVerified: org.isVerified,
        breedSpecialty:
            org.breedNames.length > 0 ? org.breedNames.join(", ") : undefined,
        city,
        distanceLabel: formatDistance(distanceMiles, "narrative") ?? undefined,
        photos: org.media
            .map((m) => m.url)
            .filter((u): u is string => Boolean(u)),
        reviewCount: reviewCount ?? null,
        inBusinessSinceYear: org.inBusinessSince
            ? new Date(org.inBusinessSince).getFullYear()
            : null,
        listingCount: listingCount ?? null,
        description: org.description?.trim() || undefined,
        website: org.website ?? undefined,
    };
}

export function useProviderSectionData(providerId: string): {
    data: ProviderData | null;
    loading: boolean;
} {
    const { data: orgs } = useProviders([providerId]);
    const org = orgs?.[0];
    const { location: userLocation } = useUserLocation();
    const listingsPage = useProviderListingReferencesInitialPage(providerId);
    const listingIds = (listingsPage.data?.listingReferences ?? []).map(
        ({ listingId }) => listingId,
    );
    usePrefetchListings(listingIds);
    const { data: listings } = useWagsListingsLiveQuery(listingIds);
    const reviewsQuery = useWagsOrganizationsReviewsLiveQuery(providerId, {
        limit: 5,
    });

    if (!org) return { data: null, loading: true };

    const coords = org.location;
    const distanceMiles =
        userLocation && coords
            ? calculateDistance(
                  userLocation.lat,
                  userLocation.lon,
                  coords.y,
                  coords.x,
              )
            : null;

    const city =
        org.city && org.state
            ? `${org.city}, ${org.state}`
            : (org.city ?? undefined);

    const providerListings = listings ?? [];
    const reviews = reviewsQuery.data?.reviews ?? [];

    return {
        loading: false,
        data: {
            ...providerIdentityData(
                org,
                city,
                distanceMiles,
                reviewsQuery.data?.total,
                listingsPage.data?.total,
            ),
            ...providerSectionCollections(providerListings, reviews),
        },
    };
}

function providerSectionCollections(
    listings: NonNullable<ReturnType<typeof useWagsListingsLiveQuery>["data"]>,
    reviews: NonNullable<
        ReturnType<typeof useWagsOrganizationsReviewsLiveQuery>["data"]
    >["reviews"],
) {
    return {
        puppies:
            listings.length > 0
                ? listings.map((l) => ({
                      id: l.id,
                      name: l.name?.trim() || "Puppy",

                      ageLabel:
                          getDogAgeDisplayVerbose(l.birthday ?? null, "") ||
                          undefined,
                      sexLabel: l.sex ?? undefined,
                      photo: l.imageUrls[0],
                      breed: l.breeds[0]?.name,
                  }))
                : undefined,
        reviews:
            reviews.length > 0
                ? reviews.map((r) => ({
                      id: r.id,
                      author: r.customerName ?? "Anonymous",
                      text: r.description ?? "",
                      timeAgo: monthsAgo(r.createdAt),
                  }))
                : undefined,
    };
}
